import { useEffect, useState } from 'react';
import { User, Shield, Bell, Palette, Key, Globe } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';
import {
  isSessionTimeoutEnabled,
  getSessionTimeoutMinutes,
  SESSION_TIMEOUT_MINUTES_KEY,
} from '../hooks/useInactivityLogout';
import { createAdminNotification } from '../services/notificationService';
import { auditApi } from '../services/auditApi';

const SESSION_TIMEOUT_STORAGE_KEY = 'adminSessionTimeoutEnabled';

function getAuditActor(): string {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'Admin';
    const user = JSON.parse(raw) as { name?: string; email?: string };
    return user?.name || user?.email || 'Admin';
  } catch {
    return 'Admin';
  }
}
const MIN_MINUTES = 1;
const MAX_MINUTES = 720;

const settingsSections = [
  {
    title: 'Profile',
    icon: User,
    description: 'Manage your account details and preferences',
  },
  {
    title: 'Security',
    icon: Shield,
    description: 'Configure authentication and access controls',
  },
  {
    title: 'Notifications',
    icon: Bell,
    description: 'Set up email and push notification preferences',
  },
  {
    title: 'Appearance',
    icon: Palette,
    description: 'Customize the admin panel theme and layout',
  },
  {
    title: 'API Keys',
    icon: Key,
    description: 'Manage API credentials and webhooks',
  },
  {
    title: 'White-Label',
    icon: Globe,
    description: 'Configure branding and deployment settings',
  },
];

export default function SettingsPage() {
  const { toast } = useToast();
  const [sessionTimeoutEnabled, setSessionTimeoutEnabled] = useState(false);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(30);

  useEffect(() => {
    setSessionTimeoutEnabled(isSessionTimeoutEnabled());
    setSessionTimeoutMinutes(getSessionTimeoutMinutes());
  }, []);

  const handleSessionTimeoutChange = (checked: boolean) => {
    localStorage.setItem(SESSION_TIMEOUT_STORAGE_KEY, String(checked));
    setSessionTimeoutEnabled(checked);
    if (checked) {
      const mins = getSessionTimeoutMinutes();
      setSessionTimeoutMinutes(mins);
      if (!localStorage.getItem(SESSION_TIMEOUT_MINUTES_KEY)) {
        localStorage.setItem(SESSION_TIMEOUT_MINUTES_KEY, String(mins));
      }
    }
    window.dispatchEvent(new CustomEvent('adminSessionTimeoutChanged'));
    const mins = getSessionTimeoutMinutes();
    toast({
      title: checked ? 'Session timeout enabled' : 'Session timeout disabled',
      description: checked
        ? `You will be logged out after ${mins} minute${mins !== 1 ? 's' : ''} of inactivity.`
        : 'You will stay logged in until you log out or your token expires.',
    });
    createAdminNotification({
      type: 'alert',
      title: 'Settings updated',
      message: checked
        ? `Session timeout was enabled (${mins} min inactivity).`
        : 'Session timeout was disabled.',
      priority: 'low',
    });
    auditApi
      .create({
        action: checked ? 'Session timeout enabled' : 'Session timeout disabled',
        category: 'settings',
        actor: getAuditActor(),
        details: checked
          ? `Session timeout enabled. Auto-logout after ${mins} minute${mins !== 1 ? 's' : ''} of inactivity.`
          : 'Session timeout disabled. User stays logged in until manual logout or token expiry.',
        severity: 'info',
      })
      .catch(() => {});
  };

  const handleSessionTimeoutMinutesChange = (value: string) => {
    if (value === '') {
      setSessionTimeoutMinutes(30);
      return;
    }
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) return;
    const clamped = Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, n));
    setSessionTimeoutMinutes(clamped);
  };

  const handleSessionTimeoutMinutesBlur = () => {
    const current = sessionTimeoutMinutes;
    if (Number.isNaN(current) || current < MIN_MINUTES || current > MAX_MINUTES) {
      setSessionTimeoutMinutes(30);
      localStorage.setItem(SESSION_TIMEOUT_MINUTES_KEY, '30');
      window.dispatchEvent(new CustomEvent('adminSessionTimeoutChanged'));
      return;
    }
    const clamped = Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, current));
    setSessionTimeoutMinutes(clamped);
    localStorage.setItem(SESSION_TIMEOUT_MINUTES_KEY, String(clamped));
    window.dispatchEvent(new CustomEvent('adminSessionTimeoutChanged'));
    toast({
      title: 'Session timeout updated',
      description: `You will be logged out after ${clamped} minute${clamped !== 1 ? 's' : ''} of inactivity.`,
    });
    auditApi
      .create({
        action: 'Session timeout duration updated',
        category: 'settings',
        actor: getAuditActor(),
        details: `Session timeout set to ${clamped} minute${clamped !== 1 ? 's' : ''} of inactivity.`,
        severity: 'info',
      })
      .catch(() => {});
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold text-foreground'>Settings</h1>
        <p className='text-muted-foreground'>Manage your account and platform configuration.</p>
      </div>

      {/* Settings Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        {settingsSections.map((section) => {
          const IconComponent = section.icon;
          return (
            <div
              key={section.title}
              className='glass-card rounded-xl p-6 transition-all duration-300 hover:border-primary/30 cursor-pointer group'
            >
              <div className='flex items-start gap-4'>
                <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-primary/20 text-primary group-hover:bg-primary/30 transition-colors'>
                  <IconComponent className='w-6 h-6' />
                </div>
                <div>
                  <h3 className='font-semibold text-foreground group-hover:text-primary transition-colors'>
                    {section.title}
                  </h3>
                  <p className='text-sm text-muted-foreground mt-1'>{section.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Settings */}
      <div className='glass-card rounded-xl p-6'>
        <h3 className='text-lg font-semibold text-foreground mb-4'>Quick Settings</h3>
        <div className='space-y-4'>
          <div className='flex items-center justify-between py-3 border-b border-border'>
            <div>
              <p className='font-medium text-foreground'>Email Notifications</p>
              <p className='text-sm text-muted-foreground'>Receive alerts for important events</p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className='flex items-center justify-between py-3 border-b border-border'>
            <div>
              <p className='font-medium text-foreground'>Two-Factor Authentication</p>
              <p className='text-sm text-muted-foreground'>Require 2FA for all admin actions</p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className='flex items-center justify-between py-3 border-b border-border'>
            <div>
              <p className='font-medium text-foreground'>Session Timeout</p>
              <p className='text-sm text-muted-foreground'>
                Auto-logout after a period of inactivity (default 30 minutes)
              </p>
            </div>
            <Switch checked={sessionTimeoutEnabled} onCheckedChange={handleSessionTimeoutChange} />
          </div>
          {sessionTimeoutEnabled && (
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3'>
              <div>
                <p className='font-medium text-foreground'>Timeout (minutes)</p>
                <p className='text-sm text-muted-foreground'>
                  Enter 1–720. After this many minutes without activity you will be logged out.
                </p>
              </div>
              <Input
                type='number'
                min={MIN_MINUTES}
                max={MAX_MINUTES}
                value={sessionTimeoutMinutes}
                onChange={(e) => handleSessionTimeoutMinutesChange(e.target.value)}
                onBlur={handleSessionTimeoutMinutesBlur}
                className='w-24 shrink-0'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
